import { OrganizationUnitStatus } from '@today/types'
import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ErrorText, SelectInput, TextArea, TextInput } from '../product/product-fields'
import { CheckboxInput, DateInput } from './org-fields'
import type { OrgEditorState, OrgOption, OrgUnitFormValues, Translate } from './org-types'

type TextField = 'code' | 'name' | 'shortName' | 'externalId' | 'institutionCode' | 'countryCode' | 'timezone'

/**
 * Props for {@link OrgUnitFormView}.
 *
 * @property editor - The open create/edit dialog, or null. In edit mode code and parent are read-only.
 * @property typeOptions - The tenant's unit types (value = code).
 * @property parentLabel - The parent shown read-only (create under a parent, or edit).
 */
export interface OrgUnitFormViewProps {
	t: Translate
	editor: OrgEditorState<OrgUnitFormValues> | null
	typeOptions: OrgOption[]
	parentLabel: string
	isBusy: boolean
	onChange: <K extends keyof OrgUnitFormValues>(field: K, value: OrgUnitFormValues[K]) => void
	onSubmit: () => void
	onClose: () => void
}

type FieldsProps = { props: OrgUnitFormViewProps; editor: OrgEditorState<OrgUnitFormValues> }

const textField = ({ props, editor }: FieldsProps, name: TextField, disabled = false) => (
	<TextInput
		label={props.t(`unit.fields.${name}`)}
		value={editor.values[name]}
		error={editor.errors[name]}
		disabled={disabled}
		onChange={(v) => props.onChange(name, v)}
	/>
)

function IdentityFields(fp: FieldsProps): ReactElement {
	const { props, editor } = fp
	const { t } = props
	return (
		<>
			{textField(fp, 'code', editor.mode === 'edit')}
			{textField(fp, 'name')}
			<SelectInput
				label={t('unit.fields.typeCode')}
				value={editor.values.typeCode}
				emptyLabel=""
				options={props.typeOptions}
				error={editor.errors.typeCode}
				onChange={(v) => props.onChange('typeCode', v)}
			/>
			<p className="text-ds-sm">
				<span className="text-ds-text-muted">{t('unit.fields.parent')}: </span>
				{props.parentLabel}
			</p>
			<SelectInput
				label={t('unit.fields.status')}
				value={editor.values.status}
				options={Object.values(OrganizationUnitStatus).map((s) => ({ id: s, label: t(`status.${s}`) }))}
				onChange={(v) => props.onChange('status', v as OrganizationUnitStatus)}
			/>
			<CheckboxInput
				label={t('unit.fields.isPhysical')}
				checked={editor.values.isPhysical}
				onChange={(v) => props.onChange('isPhysical', v)}
			/>
		</>
	)
}

function DetailFields(fp: FieldsProps): ReactElement {
	const { props, editor } = fp
	const { t } = props
	return (
		<>
			{textField(fp, 'shortName')}
			{textField(fp, 'externalId')}
			{textField(fp, 'institutionCode')}
			{textField(fp, 'countryCode')}
			{textField(fp, 'timezone')}
			<DateInput
				label={t('unit.fields.validFrom')}
				value={editor.values.validFrom}
				error={editor.errors.validFrom}
				onChange={(v) => props.onChange('validFrom', v)}
			/>
			<DateInput
				label={t('unit.fields.validTo')}
				value={editor.values.validTo}
				error={editor.errors.validTo}
				onChange={(v) => props.onChange('validTo', v)}
			/>
			<TextArea
				label={t('unit.fields.metadata')}
				value={editor.values.metadata}
				error={editor.errors.metadata}
				onChange={(v) => props.onChange('metadata', v)}
			/>
		</>
	)
}

/** The unit create/edit dialog (organism). */
export function OrgUnitFormView(props: OrgUnitFormViewProps): ReactElement | null {
	const { t, editor } = props
	if (!editor) return null
	const title = t(editor.mode === 'create' ? 'unit.dialogNew' : 'unit.dialogEdit')
	return (
		<Dialog isOpen onOpenChange={(open) => (open ? undefined : props.onClose())} title={title}>
			<form
				aria-label={title}
				className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto"
				onSubmit={(e) => {
					e.preventDefault()
					props.onSubmit()
				}}
			>
				<IdentityFields props={props} editor={editor} />
				<DetailFields props={props} editor={editor} />
				<ErrorText message={editor.formError} />
				<div className="flex gap-2">
					<Button type="submit" isDisabled={props.isBusy}>
						{t(editor.mode === 'create' ? 'unit.create' : 'unit.save')}
					</Button>
					<Button variant="muted" onPress={props.onClose}>
						{t('unit.cancel')}
					</Button>
				</div>
			</form>
		</Dialog>
	)
}
